import { computed, Injectable, inject, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Option } from '@sapphire/result';

import { AccessLevel } from '@ps/core-models';

import {
  selectLoading,
  selectError,
  selectUser,
  selectFailedLoginAttempts,
  selectIsAuthenticated,
  selectUserType,
  selectIsMultiBuyer,
  selectIsBuyer,
  selectIsOperator,
  selectUserAccessLevel,
  selectRememberMe,
  selectHasServiceOperatorAccess,
} from './auth-session/auth-session.selectors';
import {
  selectSsoIsCheckingEmail,
  selectSsoIsExchangingCode,
  selectSsoError,
} from './auth-sso/auth-sso.selectors';
import {
  selectHasImpersonatedBuyer,
  selectImpersonatedBuyer,
  selectIsImpersonationLoading,
} from './impersonation/impersonation.selectors';

@Injectable({ providedIn: 'root' })
export class AuthSelectorsFacade {
  private readonly _store = inject(Store);

  public readonly loading = this._store.selectSignal(selectLoading);

  public readonly error = this._store.selectSignal(selectError);

  public readonly user = computed(() =>
    Option.from(this._store.selectSignal(selectUser)()),
  );

  public readonly isAuthenticated = this._store.selectSignal(
    selectIsAuthenticated,
  );

  public readonly userType = this._store.selectSignal(selectUserType);

  public readonly userAccessLevel: Signal<AccessLevel> =
    this._store.selectSignal(selectUserAccessLevel);

  public readonly isBuyer = this._store.selectSignal(selectIsBuyer);
  public readonly isOperator = this._store.selectSignal(selectIsOperator);
  public readonly isMultiBuyer = this._store.selectSignal(selectIsMultiBuyer);

  // Impersonation - Service Operator

  public readonly hasServiceOperatorAccess = this._store.selectSignal(
    selectHasServiceOperatorAccess,
  );

  public readonly hasImpersonatedBuyer = this._store.selectSignal(
    selectHasImpersonatedBuyer,
  );

  public readonly isBuyerOrImpersonatingBuyer = computed(
    () => this.isBuyer() || this.hasImpersonatedBuyer(),
  );

  public readonly impersonationLoading = this._store.selectSignal(
    selectIsImpersonationLoading,
  );

  public readonly impersonatedBuyer = this._store.selectSignal(
    selectImpersonatedBuyer,
  );

  public readonly activeCompanyId = computed(() => {
    // When using this selector, the user is guaranteed to be logged in. Otherwise, the selector will throw.
    const userCompany = this.user()
      .map((user) => user?.companyId)
      .unwrap();

    // When impersonating a buyer, use the impersonated buyer's ID (buyer company ID).
    return Option.from(this.impersonatedBuyer())
      .map((impersonatedBuyer) => impersonatedBuyer?.id)
      .unwrapOr(userCompany);
  });

  // Login
  public readonly failedLoginAttempts = this._store.selectSignal(
    selectFailedLoginAttempts,
  );

  public readonly rememberMe = this._store.selectSignal(selectRememberMe);

  // SSO / OIDC
  public readonly ssoIsCheckingEmail = this._store.selectSignal(
    selectSsoIsCheckingEmail,
  );

  public readonly ssoIsExchangingCode = this._store.selectSignal(
    selectSsoIsExchangingCode,
  );

  public readonly ssoError = computed(() =>
    Option.from(this._store.selectSignal(selectSsoError)()),
  );
}
